import type {MatchParams} from '@wandb/weave/common/types/base';

import type {ContentPart} from '../store/thread';
import {collectAgentPageContext} from './collect';

/**
 * PromptContextParams describes the user's current route scope (entity,
 * project, run, and normalized path) as captured by React Router. The runtime
 * hook consumes this on every user message to derive wandb_web_page_state.
 */
export type PromptContextParams = MatchParams & {
  /**
   * Current path in the app's router. Unlike `url`, this is stable across
   * standalone wandb.ai and Forge (shell prefix stripped client-side).
   */
  appPath?: string;
  /** Stable identity of the product serving the current Forge page. */
  productId?: string;
};

/**
 * buildPromptContextParts produces the wandb-web-page-state content part
 * describing the user's current page (URL, entity/project, and any captured
 * page content) at send time.
 */
export function buildPromptContextParts({
  entityName,
  projectName,
  appPath,
  productId,
}: PromptContextParams): ContentPart[] {
  const pageContent = collectAgentPageContext();
  const hasProject = !!(entityName && projectName);
  return [
    {
      type: 'wandb-web-page-state',
      content: pageContent.length > 0 ? pageContent : undefined,
      entityName: entityName || undefined,
      projectName: hasProject ? projectName : undefined,
      productId,
      appPath: appPath || undefined,
      // NOTE: Drop query/hash to limit prompt-injection and PII leakage.
      url: window.location.origin + window.location.pathname,
    },
  ];
}
